import moment from 'moment'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import InfiniteScroll from '../../infinite-scroll/InfiniteScroll'

import { ReactComponent as OutsideLinkIcon } from '../../../assets/images/common/icons/outside-link-icon.svg'
import { ReactComponent as SortIcon } from '../../../assets/images/common/icons/sort-icon.svg'
import ProfileAvatar from '../../common/avatar/ProfileAvatar'
import Loader from '../../common/loader/Loader'
import LoadingSpinner from '../../common/loader/legacy-loaders/LoadingSpinner'
import BillingStatusBadge from './BillingStatusBadge'

import classes from './EnrollmentsBillingTable.module.scss'
import colors from '../../../assets/styles/globals.scss'

import {
  getEnrollmentsColumns, getSortStates, enrollmentStatusLabel,
  formatPaymentStatusDisplay, formatInvoiceStatusDisplay, formatMembershipStatusDisplay, formatPaymentMethodDisplay,
  activityLabel,
} from '../../../utility/enrollmentsBilling'
import { displayWithCurrency } from '../../../utility/currency'
import { genderLabel } from '../../../utility/userHelper'
import { useSelector } from 'react-redux'


// Payment method label: from the invoice when one exists, otherwise from the enrollment's own
// card/offline payment field.
const resolvePaymentMethodLabel = (enrollment, t) => {
  if (enrollment.invoice) {
    return formatPaymentMethodDisplay(enrollment.invoice, t) || t('enrollmentsBilling.table.onlineOrOffline')
  }
  if (enrollment.paymentMethod === 'card') { return t('enrollmentsBilling.table.online') }
  return enrollment.paymentMethod ? t('enrollmentsBilling.table.offline') : null
}

const SortButton = ({ label, active, onClick, loading }) => (
  <button className={classNames(classes.sortButton, { [classes.active]: active })} onClick={onClick} disabled={loading}>
    {label}
    {loading ? (
      <LoadingSpinner classNames={{ wrapper: classes.spinnerWrapper }} />
    ) : (
      <SortIcon className={classes.sortIcon} width={16} height={16} color={colors.slateGrayIcons} />
    )}
  </button>
)

SortButton.propTypes = {
  label: PropTypes.string,
  active: PropTypes.bool,
  loading: PropTypes.bool,
  onClick: PropTypes.func,
}

const formatProgramDates = (course) => {
  if (!course?.startDate) { return '-' }

  const start = moment(course.startDate).format('MM/DD/YYYY')
  const end = course.endDate ? ` - ${moment(course.endDate).format('MM/DD/YYYY')}` : ''
  return `${start}${end}`
}

const LatestUpdate = ({ enrollment }) => {
  const { t } = useTranslation()
  const { latestActivity } = enrollment
  if (!latestActivity) { return <span>-</span> }

  const label = activityLabel(latestActivity.changeType, t)
  const date = latestActivity.createdAt ? moment(latestActivity.createdAt).format('MM/DD/YYYY') : ''
  return (
    <div className={classes.latestUpdate}>
      <p className={classes.latestUpdateLabel}>{label}</p>
      <p className={classes.latestUpdateDate}>{date}</p>
    </div>
  )
}

LatestUpdate.propTypes = {
  enrollment: PropTypes.shape({
    latestActivity: PropTypes.shape({
      changeType: PropTypes.string,
      createdAt: PropTypes.string,
    }),
  }).isRequired,
}

// eslint-disable-next-line max-lines-per-function
const EnrollmentsBillingTable = ({
  enrollments, fetchData, pagination, onRowClick, selectedEnrollmentId,
  isExporting, isLoading, isSortLoading, sortOption, applySort,
  onApprove, onDecline, hideProgramColumns = false,
}) => {
  const { t } = useTranslation()
  const hasMore = !pagination?.totalPages || (pagination?.page + 1) <= pagination?.totalPages
  const currency = useSelector(state => state.user.current_user.communities[0].currency)
  const consumerPlan = useSelector(state => state.user.current_user.communities[0].license_type === 'consumer_plan')
  // A single-program view (dashboard tab) drops the Program Name + Program Dates columns.
  const hiddenFields = [
    ...(hideProgramColumns ? ['program_name', 'program_dates'] : []),
    ...(consumerPlan ? [] : ['membership_status']),
  ]
  const columns = getEnrollmentsColumns(t).filter(({ field }) => !hiddenFields.includes(field))

  if (!enrollments || !pagination) {
    return (
      <div className='h-100 d-flex align-items-center justify-content-center'>
        <Loader>{t('enrollmentsBilling.table.loadingEnrollments')}</Loader>
      </div>
    )
  }

  const sortStates = getSortStates()

  const getNextSortValue = (selectedSortField, currentSortValue) => {
    if (selectedSortField !== sortOption.sortField) {
      return sortStates.at(0)
    }
    if (currentSortValue === sortStates.at(-1)) {
      return null
    }
    const currentIndex = sortStates.indexOf(currentSortValue)
    return sortStates[(currentIndex + 1) % sortStates.length]
  }

  return (
    <div id='enrollmentsBillingTable' className={classes.table}>
      <InfiniteScroll
        dataLength={enrollments.length}
        next={fetchData}
        hasMore={hasMore}
        scrollableTarget='enrollmentsTableContent'
        className={classes.tableContent}
        hideLoader
      >
        <div
          className={classNames(classes.gridTable, {
            [classes.gridTableProgram]: hideProgramColumns,
            [classes.gridTableMembership]: consumerPlan,
          })}
          id='enrollmentsTableContent'
        >
          {/* Fixed first column header: Student */}
          <div className={classNames(classes.col, classes.header, classes.firstColumn, classes.topLeft)}>
            <span className='font-weight-bold'>{t('enrollmentsBilling.table.enrollmentsCount', { total: pagination?.totalCount })}</span>
          </div>

          {/* Column headers (sortable unless flagged otherwise) */}
          {columns.map(({ label, field, sortable }, idx) => (
            <div
              className={classNames(classes.col, classes.header, { [classes.headerProgram]: field === 'program_name' })}
              key={idx}
            >
              {sortable === false ? (
                <span className={classes.headerLabel}>{label}</span>
              ) : (
                <SortButton
                  label={label}
                  active={sortOption.sortField === field && sortOption.sortValue !== null}
                  onClick={() => applySort(field, getNextSortValue(field, sortOption.sortValue))}
                  loading={isSortLoading && sortOption.sortField === field}
                />
              )}
            </div>
          ))}

          {isExporting ? (
            <div className={classes.exportLoadingWrapper}>
              <Loader loaderType='ball-beat' loaderColor={colors.lightNavy}>{t('enrollmentsBilling.table.exportingEnrollments')}</Loader>
              <span className='text-center'>{t('enrollmentsBilling.table.exportDurationNotice')}</span>
            </div>
          ) : (
            <>
              {enrollments.map(enrollment => {
                const { student, course, invoice } = enrollment
                const displayStatus = enrollment.displayStatus
                // Billing status / amount / payment method come from the invoice when one exists,
                // falling back to the enrollment's own payment fields otherwise.
                const billingDisplay = invoice ? formatInvoiceStatusDisplay(invoice, t) : formatPaymentStatusDisplay(enrollment.paymentStatus, t)
                const amount = invoice ? invoice.total : enrollment.amount
                const paymentMethodLabel = resolvePaymentMethodLabel(enrollment, t)

                return (
                  <div
                    tabIndex={0}
                    key={enrollment.id}
                    role='button'
                    className={classNames(classes.row, { [classes.isClicked]: enrollment.id === selectedEnrollmentId })}
                    onClick={() => onRowClick(enrollment)}
                    onKeyDown={() => null}
                  >
                    {/* Student (fixed first column) */}
                    <div
                      className={classNames(classes.col, classes.firstColumn, classes.studentInfo)}
                      title={`${student.firstName} ${student.lastName}`}
                    >
                      <ProfileAvatar student={student} avatarClassName='small-round-image-wrapper' />
                      <div className='d-flex flex-column flex-grow-1 pr-3'>
                        <h6 className='mb-1 font-weight-bold'>{student.firstName} {student.lastName}</h6>
                        <span>
                          {student.age !== null && t('enrollmentsBilling.table.ageYears', { age: student.age })}
                          {student.age !== null && student.gender !== null && '/'}
                          {genderLabel[student.gender]}
                        </span>
                      </div>
                      <OutsideLinkIcon className={classes.detailsIcon} />
                    </div>

                    {/* Program */}
                    {!hideProgramColumns && (
                      <div className={classNames(classes.col, classes.programCol)}>
                        <div className={classes.programCell}>
                          <span className={classes.programName} title={course?.name}>
                            {course?.name || '-'}
                          </span>
                          {course?.meetingTimes && (
                            <span className={classes.programMeetingTimes}>{course.meetingTimes}</span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Enrollment Status */}
                    <div className={classNames(classes.col, classes.centeredCol)}>
                      {displayStatus === 'pending_approval' ? (
                        <div className={classes.approvalActions}>
                          <button
                            className={classes.approveButton}
                            onClick={(e) => { e.stopPropagation(); onApprove?.(enrollment) }}
                          >
                            {t('enrollmentsBilling.table.approve')}
                          </button>
                          <button
                            className={classes.declineButton}
                            onClick={(e) => { e.stopPropagation(); onDecline?.(enrollment) }}
                          >
                            {t('enrollmentsBilling.table.decline')}
                          </button>
                        </div>
                      ) : (
                        <span className={classNames(
                          classes.statusBadge,
                          {
                            [classes.statusActive]: displayStatus === 'active',
                            [classes.statusFuture]: displayStatus === 'future',
                            [classes.statusCompleted]: displayStatus === 'completed',
                            [classes.statusDropped]: displayStatus === 'dropped',
                            [classes.statusWaitlisted]: displayStatus === 'waitlisted',
                          }
                        )}>
                          {enrollmentStatusLabel(displayStatus, t)}
                        </span>
                      )}
                    </div>

                    {/* Billing Status (invoice status when billed, else enrollment payment status) */}
                    <div className={classNames(classes.col, classes.centeredCol)}>
                      <BillingStatusBadge display={billingDisplay} className={classes.billingBadge} />
                    </div>

                    {consumerPlan && (
                      <div className={classNames(classes.col, classes.centeredCol)}>
                        {student.membershipActive === undefined ? (
                          <span>-</span>
                        ) : (
                          <BillingStatusBadge
                            display={formatMembershipStatusDisplay(student.membershipActive, t)}
                            className={classes.billingBadge}
                          />
                        )}
                      </div>
                    )}

                    {/* Latest Update */}
                    <div className={classNames(classes.col, classes.centeredCol)}>
                      <LatestUpdate enrollment={enrollment} />
                    </div>

                    {/* Amount (invoice total when billed, else enrollment amount) */}
                    <div className={classNames(classes.col, classes.centeredCol)}>
                      <span className='font-weight-bold'>
                        {amount !== null && amount !== undefined ? displayWithCurrency(amount, currency, true) : '-'}
                      </span>
                    </div>

                    {/* Payment Method (invoice payment method when billed, else enrollment) */}
                    <div className={classNames(classes.col, classes.centeredCol)}>
                      <span>{paymentMethodLabel || '-'}</span>
                    </div>

                    {/* Package */}
                    <div className={classNames(classes.col, classes.centeredCol)}>
                      <span className='text-truncate' title={enrollment.package?.name}>
                        {enrollment.package?.name || '-'}
                      </span>
                    </div>

                    {/* Age */}
                    <div className={classNames(classes.col, classes.centeredCol)}>
                      <span>{student.age ?? '-'}</span>
                    </div>

                    {/* Level */}
                    <div className={classNames(classes.col, classes.centeredCol)}>
                      <span>{student.level !== null ? t('enrollmentsBilling.table.levelAbbreviation', { level: student.level }) : '-'}</span>
                    </div>

                    {/* Gender */}
                    <div className={classNames(classes.col, classes.centeredCol)}>
                      <span>{genderLabel[student.gender] || '-'}</span>
                    </div>

                    {/* Program Dates */}
                    {!hideProgramColumns && (
                      <div className={classNames(classes.col, classes.centeredCol)}>
                        <span>{formatProgramDates(course)}</span>
                      </div>
                    )}
                  </div>
                )
              })}

              {/* Skeleton loading rows */}
              {!!(hasMore && isLoading) && Array.from({ length: 3 }, (_, idx) => (
                <div className={classNames(classes.row, classes.skeletons)} key={`skeleton-${idx}`}>
                  <div className={classNames(classes.col, classes.firstColumn, classes.studentInfo)}>
                    <div data-skeleton='avatar' />
                    <div className={classNames('d-flex flex-column flex-grow-1 pr-3', classes.skeletonInfoWrapper)}>
                      <div data-skeleton='name' />
                      <div data-skeleton='info' />
                    </div>
                  </div>
                  {columns.map((_, colIdx) => (
                    <div className={classNames(classes.col, classes.centeredCol)} key={colIdx}>
                      <div data-skeleton='longValue' />
                    </div>
                  ))}
                </div>
              ))}
            </>
          )}

        </div>
      </InfiniteScroll>
    </div>
  )
}

EnrollmentsBillingTable.propTypes = {
  enrollments: PropTypes.array,
  fetchData: PropTypes.func.isRequired,
  onRowClick: PropTypes.func,
  onApprove: PropTypes.func,
  onDecline: PropTypes.func,
  pagination: PropTypes.object,
  selectedEnrollmentId: PropTypes.number,
  isExporting: PropTypes.bool,
  isLoading: PropTypes.bool,
  isSortLoading: PropTypes.bool,
  sortOption: PropTypes.object,
  applySort: PropTypes.func,
  hideProgramColumns: PropTypes.bool,
}

export default EnrollmentsBillingTable
